import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant } from '../../../theme/tokens';
import { PlusIcon, SearchIcon, QrCodeIcon, CheckCircleIcon, PauseCircleIcon, ScanLineIcon, MoreHorizontalIcon, EyeIcon, CopyIcon, DownloadIcon, PauseIcon, PlayIcon, TrashIcon, XIcon } from 'lucide-react';
type QRStatus = 'active' | 'paused' | 'expired';
interface QRCodeEntry {
  id: string;
  serial: string;
  label: string;
  location: string;
  status: QRStatus;
  totalScans: number;
  scansLast30d: number;
  totalCollected: number; // in cents
  createdAt: string; // ISO date
  lastScanAt: string | null;
}
const PRIMARY = '#0F4C9C';
// Deterministic decorative QR (purely visual)
function decorativeQR(seed: string, size = 200) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = h * 31 + seed.charCodeAt(i) | 0;
  const grid = 29;
  const cells: boolean[] = [];
  for (let i = 0; i < grid * grid; i++) {
    h = h * 1103515245 + 12345 & 0x7fffffff;
    cells.push(h % 100 < 48);
  }
  const cell = size / grid;
  const rects: string[] = [];
  for (let y = 0; y < grid; y++) {
    for (let x = 0; x < grid; x++) {
      const inTopLeft = x < 8 && y < 8;
      const inTopRight = x >= grid - 8 && y < 8;
      const inBottomLeft = x < 8 && y >= grid - 8;
      if (inTopLeft || inTopRight || inBottomLeft) continue;
      if (cells[y * grid + x]) {
        rects.push(`<rect x="${x * cell}" y="${y * cell}" width="${cell}" height="${cell}" fill="#fff" />`);
      }
    }
  }
  const finder = (fx: number, fy: number) => `
    <rect x="${fx * cell}" y="${fy * cell}" width="${cell * 7}" height="${cell * 7}" fill="#fff"/>
    <rect x="${(fx + 1) * cell}" y="${(fy + 1) * cell}" width="${cell * 5}" height="${cell * 5}" fill="${PRIMARY}"/>
    <rect x="${(fx + 2) * cell}" y="${(fy + 2) * cell}" width="${cell * 3}" height="${cell * 3}" fill="#fff"/>
  `;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <rect width="${size}" height="${size}" fill="${PRIMARY}"/>
    ${rects.join('')}
    ${finder(0, 0)}
    ${finder(grid - 7, 0)}
    ${finder(0, grid - 7)}
  </svg>`;
}
export function QRCodes() {
  const navigate = useNavigate();
  const currency = getTenant().currency.code;
  const [qrCodes, setQrCodes] = useState<QRCodeEntry[]>([{
    id: 'qr-1',
    serial: 'JWPH63S4WX',
    label: 'Main terminal',
    location: 'BML Experience Zone',
    status: 'active',
    totalScans: 1245,
    scansLast30d: 312,
    totalCollected: 4520050,
    createdAt: '2025-11-12',
    lastScanAt: '2026-05-18'
  }, {
    id: 'qr-2',
    serial: 'JTKINHTYZ7',
    label: 'Counter 2',
    location: 'BML Experience Zone',
    status: 'active',
    totalScans: 873,
    scansLast30d: 198,
    totalCollected: 2890075,
    createdAt: '2025-12-04',
    lastScanAt: '2026-05-19'
  }, {
    id: 'qr-3',
    serial: 'KZQ4HX9P2L',
    label: 'Pop-up booth',
    location: 'Subang Jaya branch',
    status: 'paused',
    totalScans: 412,
    scansLast30d: 0,
    totalCollected: 845200,
    createdAt: '2026-01-18',
    lastScanAt: '2026-04-02'
  }, {
    id: 'qr-4',
    serial: 'M3RT8YA1NX',
    label: 'Ramadan promo',
    location: 'Main branch lobby',
    status: 'expired',
    totalScans: 2104,
    scansLast30d: 0,
    totalCollected: 7654000,
    createdAt: '2025-09-01',
    lastScanAt: '2026-03-15'
  }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | QRStatus>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [zoomTarget, setZoomTarget] = useState<QRCodeEntry | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filtered = useMemo(() => {
    return qrCodes.filter(qr => {
      const matchesSearch = !searchQuery || qr.label.toLowerCase().includes(searchQuery.toLowerCase()) || qr.serial.toLowerCase().includes(searchQuery.toLowerCase()) || qr.location.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = activeTab === 'all' || qr.status === activeTab;
      return matchesSearch && matchesStatus;
    });
  }, [qrCodes, searchQuery, activeTab]);
  const totalQR = qrCodes.length;
  const activeQR = qrCodes.filter(q => q.status === 'active').length;
  const totalScans30d = qrCodes.reduce((sum, q) => sum + q.scansLast30d, 0);
  const totalCollected30dMinor = qrCodes.filter(q => q.status !== 'expired').reduce((sum, q) => sum + q.totalCollected, 0);
  const formatMoney = (minor: number) => (minor / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2
  });
  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB');
  };
  const getStatusBadge = (status: QRStatus) => {
    const styles: Record<QRStatus, string> = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-amber-100 text-amber-800',
      expired: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-9351" data-mp="3pxvnir8yffym">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const handleCopy = (qr: QRCodeEntry) => {
    navigator.clipboard?.writeText(`https://pay.bilabila.co/q/${qr.serial}`).catch(() => {});
    setCopiedId(qr.id);
    setTimeout(() => setCopiedId(null), 1500);
  };
  const handleDownload = (qr: QRCodeEntry) => {
    const svg = decorativeQR(qr.serial, 512);
    const blob = new Blob([svg], {
      type: 'image/svg+xml'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paymv-qr-${qr.serial.toLowerCase()}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const toggleStatus = (id: string) => {
    setQrCodes(codes => codes.map(qr => qr.id === id ? {
      ...qr,
      status: qr.status === 'active' ? 'paused' : qr.status === 'paused' ? 'active' : qr.status
    } : qr));
  };
  const handleDelete = (id: string) => {
    setQrCodes(codes => codes.filter(qr => qr.id !== id));
  };
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-9352" data-mp="1wdkanrhb9s1l">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-9353" data-mp="1wbg8bdz2913f">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-9354" data-mp="2b1f5peisk8hu">QR codes</h1>
        <div className="flex items-center gap-3" data-id="element-9355" data-mp="262u358g2hcq8">
          <button onClick={() => {
          /* future: open create flow */
        }} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-9356" data-mp="2645i84eyq6wn">
            Create QR code
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-9357" data-mp="1nccqsefjktr1">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9358" data-mp="1neyxkbulz587">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9359" data-mp="32vpi7fm4s88v">
            <QrCodeIcon size={16} className="text-gray-500" data-id="element-9360" data-mp="2yrrhx1l2kre8" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9361" data-mp="w6f833ig2jki">
              Total QR codes
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9362" data-mp="114qibfcniw39">
            <div className="text-2xl font-bold text-gray-900" data-id="element-9363" data-mp="1630d3ougruoc">{totalQR}</div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-9364" data-mp="1au0aev7hwerd">
            All time
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9365" data-mp="1euu09u4i03mw">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9366" data-mp="1jtx0905mfjo0">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-9367" data-mp="1ov2y1ce51x4e" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9368" data-mp="1tt4j22v4k6ii">Active</span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9369" data-mp="3qleemva2xm0s">
            <div className="text-2xl font-bold text-green-600" data-id="element-9370" data-mp="3qmpsjnx8oa5m">{activeQR}</div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-9371" data-mp="3vliuqk72seqh">
            Available for scanning
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9372" data-mp="2cr7qea5s44pp">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9373" data-mp="27wtr3wnzxdfr">
            <ScanLineIcon size={16} className="text-gray-500" data-id="element-9374" data-mp="23uykqdr7k9v4" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9375" data-mp="1yz9jm5zjikwp">
              Scans (30d)
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9376" data-mp="2vm4s9f99d9uf">
            <div className="text-2xl font-bold text-gray-900" data-id="element-9377" data-mp="2rm40ksx5f25f">
              {totalScans30d.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-9378" data-mp="2mpnfy8p5r5x3">
            Across {activeQR} active codes
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9379" data-mp="1b8nn9bywep2j">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9380" data-mp="16918uz7c32s5">
            <PauseCircleIcon size={16} className="text-amber-500" data-id="element-9381" data-mp="1kup3donnvzme" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9382" data-mp="1ksw1emnzu3kq">
              Collected
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9383" data-mp="1uljvh9nl9tfr">
            <div className="text-2xl font-bold text-gray-900" data-id="element-9384" data-mp="1umcshiwqx4kp">
              {currency} {formatMoney(totalCollected30dMinor)}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-9385" data-mp="23kx5q4jjem1j">
            Lifetime via QR
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-9386" data-mp="1yopvhzk3yods">
        {/* Tabs + Search */}
        <div className="flex items-end justify-between border-b border-gray-200" data-id="element-9387" data-mp="2dil0uvl21vk8">
          <div className="flex space-x-6" role="tablist" aria-label="QR codes" data-id="element-9388" data-mp="28kjk4hb3q8bx">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'active',
            label: 'Active'
          }, {
            id: 'paused',
            label: 'Paused'
          }, {
            id: 'expired',
            label: 'Expired'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => setActiveTab(tab.id as 'all' | QRStatus)} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-9389" data-mp="2g3anwnb22gge">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center gap-4 pb-3" data-id="element-9390" data-mp="223rhq01t1f6q">
            <div className="relative" data-id="element-9391" data-mp="226dxgmyun101">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-9392" data-mp="3rj7ox9w64qtz" />
              <input type="search" aria-label="Search QR codes" placeholder="Search QR codes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-9393" data-mp="atqg8noeunw" />
            </div>
          </div>
        </div>

        {/* Table list */}
        {filtered.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-9394" data-mp="cnxjx6fp9ack">
            <p className="text-gray-500 text-sm" data-id="element-9395" data-mp="7ve74bou6j8t">
              No QR codes match your filters.
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9396" data-mp="2vrsbxtd0uxw">
            <table className="w-full text-left border-collapse" data-id="element-9397" data-mp="10eum3rhp0tam">
              <tbody className="divide-y divide-gray-100" data-id="element-9398" data-mp="vli07p4ef0lk">
                {filtered.map(qr => <tr key={qr.id} className="hover:bg-gray-50 transition-colors" data-id="element-9399" data-mp="13wi9n9vot3dc">
                    <td className="px-5 py-4" data-id="element-9400" data-mp="1cvlw8nu2c0wl">
                      <button type="button" onClick={() => setZoomTarget(qr)} className="w-10 h-10 rounded-md border border-gray-200 overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-[#E2231A]/30 transition" aria-label="View QR" dangerouslySetInnerHTML={{
                  __html: decorativeQR(qr.serial, 40)
                }} data-id="element-9401" data-mp="1cuubk8207nl3" />
                    </td>
                    <td className="px-2 py-4" data-id="element-9402" data-mp="20ketqoqc2gtb">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-9403" data-mp="25kt3cb7l1ytg">
                        {qr.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 font-mono" data-id="element-9404" data-mp="2ajvxh0m0ykmn">
                        {qr.serial}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9405" data-mp="32cg2zdd35umf">
                      <div className="text-sm text-gray-700" data-id="element-9406" data-mp="32beheeyg1pqg">{qr.location}</div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9407" data-mp="3h2na4wxn7r7t">{getStatusBadge(qr.status)}</td>
                    <td className="px-4 py-4" data-id="element-9408" data-mp="3c1znsokczyjx">
                      <div className="text-sm font-medium text-gray-900 tabular-nums" data-id="element-9409" data-mp="2obn4yinkshek">
                        {qr.totalScans.toLocaleString('en-US')}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-9410" data-mp="2jf53psrgf16z">
                        {qr.scansLast30d.toLocaleString('en-US')} last 30d
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9411" data-mp="2td2ou7e0ohvd">
                      <div className="text-sm font-medium text-gray-900 tabular-nums" data-id="element-9412" data-mp="clkrwsjik3fg">
                        {currency} {formatMoney(qr.totalCollected)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-9413" data-mp="g38hshkzcplc">
                      <div className="text-sm text-gray-500" data-id="element-9414" data-mp="l3d3cm0qwaif">
                        Last scan {formatDate(qr.lastScanAt)}
                      </div>
                    </td>
                    <td className="px-4 py-4 w-12 text-right relative" data-id="element-9415" data-mp="686wjwtts7o1">
                      <div ref={contextMenuOpenId === qr.id ? contextMenuRef : null} data-id="element-9416" data-mp="b5ynaniqhnqn">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === qr.id ? null : qr.id);
                  }} aria-label="QR code actions" data-id="element-9417" data-mp="13st5tevzqd1h">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-9418" data-mp="1wf16tqojt3b7" />
                        </button>

                        {contextMenuOpenId === qr.id && <>
                            <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-9419" data-mp="1ne32t8e40vsm" />
                            <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-9420" data-mp="18pf8f5g02s49">
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        setZoomTarget(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9421" data-mp="zqli1wt9ixz5">
                                <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-9422" data-mp="36cr965etrmcs" />
                                View QR
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleCopy(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9423" data-mp="2ipj3vnhecdar">
                                <CopyIcon size={16} className="mr-3 text-gray-400" data-id="element-9424" data-mp="1zxqk8jo7w4fp" />
                                {copiedId === qr.id ? 'Copied!' : 'Copy link'}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleDownload(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9425" data-mp="sf9bvb4wiec5">
                                <DownloadIcon size={16} className="mr-3 text-gray-400" data-id="element-9426" data-mp="1q0x73spmeqm3" />
                                Download QR
                              </button>
                              {qr.status !== 'expired' && <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        toggleStatus(qr.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9427" data-mp="1v1b9au4jj6c7">
                                  {qr.status === 'active' ? <>
                                      <PauseIcon size={16} className="mr-3 text-gray-400" data-id="element-9428" data-mp="1zoj0s7w2itjg" />
                                      Pause
                                    </> : <>
                                      <PlayIcon size={16} className="mr-3 text-gray-400" data-id="element-9429" data-mp="1c1b3od8smqsi" />
                                      Resume
                                    </>}
                                </button>}
                              <div className="border-t border-gray-100 my-1" data-id="element-9430" data-mp="166uhdn1ld5cd" />
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleDelete(qr.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9431" data-mp="1b4cnl5o856go">
                                <TrashIcon size={16} className="mr-3" data-id="element-9432" data-mp="2hs9i1gyrhpop" />
                                Delete
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Zoom modal */}
      {zoomTarget && <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setZoomTarget(null)} data-id="element-9433" data-mp="jvt208f6l4y9">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9434" data-mp="3obkei7gb1awv">
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200" data-id="element-9435" data-mp="15th7xqdic0n">
              <div data-id="element-9436" data-mp="3sc4c1c4sigjj">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-9437" data-mp="3sbmfeee7u0b3">
                  {zoomTarget.label}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 font-mono" data-id="element-9438" data-mp="1cjoypd4r2wv1">
                  {zoomTarget.serial}
                </p>
              </div>
              <button type="button" onClick={() => setZoomTarget(null)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9439" data-mp="58hatign36bg">
                <XIcon size={18} data-id="element-9440" data-mp="ea8keyeavxal" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center" data-id="element-9441" data-mp="j8s5yxozf72f">
              <div className="rounded-xl overflow-hidden p-3 bg-white border border-gray-200" dangerouslySetInnerHTML={{
            __html: decorativeQR(zoomTarget.serial, 240)
          }} data-id="element-9442" data-mp="j65y0wy6ipjo" />
              <p className="mt-4 text-sm text-gray-500 text-center" data-id="element-9443" data-mp="3rntnrsuf64oz">
                {zoomTarget.location}
              </p>
              <div className="mt-5 flex gap-2 w-full" data-id="element-9444" data-mp="az31vecyx51">
                <button type="button" onClick={() => handleCopy(zoomTarget)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-9445" data-mp="2yzo7biaef1uy">
                  <CopyIcon size={14} data-id="element-9446" data-mp="2rh6qer070kry" />
                  {copiedId === zoomTarget.id ? 'Copied!' : 'Copy link'}
                </button>
                <button type="button" onClick={() => handleDownload(zoomTarget)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#E2231A] hover:bg-[#C41B14] rounded-lg shadow-sm transition-colors" data-id="element-9447" data-mp="2min60v18zh1p">
                  <DownloadIcon size={14} data-id="element-9448" data-mp="23prlcqh17pd0" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}